import { Field, useFormikContext, FieldProps } from 'formik';
import { camelCase } from 'lodash';
import { useCallback, useMemo } from 'react';

import { InputComponent } from '..';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Switch,
} from '@mui/material';

export interface InputCheckProps extends InputComponent {
  type?: 'checkbox' | 'switch';
  inline?: boolean;
}

export function InputCheck({
  label,
  disabled,
  hint,
  required,
  nameOverride,
  className,
  type = 'checkbox',
  inline = false,
}: InputCheckProps) {
  const { errors } = useFormikContext<Record<string, boolean>>();

  const validate = useCallback(
    (value: string | number | boolean | undefined) => {
      if (required && (!value || value === undefined)) {
        return `${label} is required.`;
      }
    },
    [label, required],
  );

  const camelLabel = useMemo(() => camelCase(label), [label]);

  const name = useMemo(
    () => nameOverride ?? camelLabel,
    [camelLabel, nameOverride],
  );

  const CheckComponent = useMemo(() => {
    if (type === 'switch') {
      return Switch;
    }
    return Checkbox;
  }, [type]);

  return (
    <FormGroup>
      <Field name={name} validate={validate} isInvalid={errors[name]}>
        {({ field }: FieldProps) => (
          <FormControlLabel
            slotProps={{ typography: { variant: 'subtitle2' } }}
            control={
              <CheckComponent
                id={`form.${name}.${camelLabel}`}
                disabled={disabled}
                required={required}
                checked={field.value}
                {...field}
                onChange={(evt) => {
                  // If the field is a checkbox, we need to set the value to true or false
                  field.onChange({
                    target: { name: field.name, value: evt.target.checked },
                  });
                }}
              />
            }
            label={label}
          />
        )}
      </Field>
      {(!!errors[name] || !!hint) && (
        <FormHelperText error={!!errors[name]}>
          {errors[name] || hint}
        </FormHelperText>
      )}
    </FormGroup>
  );
}
